import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { convertToServerPaginationIfNot } from 'legacy/lut/functions'

import { IDataProviderFilter, IDataProviderSort } from 'core-components/types'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import { Crud } from 'core/types/Crud'
import { ProcedureInnerStorageFetcher } from 'core/types/ProcedureInnerStorageFetcher'
import {
  TCreateCriteriaPayload,
  TRevenueCriteriaThresholds,
  TSpecializationEligibilityCriteriaListItem,
  TSpecializationEligibilityCriteriaWithName,
  TUpdateCriteriaPayload,
} from 'onboarding/components/widgets/public/admin/eligibilityCriteria/models'
import { IRequestSpecialization } from 'onboarding/components/widgets/public/admin/specializations/models'

const CONTROLLER = 'specializationEligibilityCriteria'

class SpecializationEligibilityCriteriaApi {
  public composeListCrud() {
    return new Crud<TSpecializationEligibilityCriteriaListItem, number>({
      fetcher: new ProcedureInnerStorageFetcher('EntryId', {
        readFromTo: async (filter, sort, paging) => {
          const pagination = convertToServerPaginationIfNot(paging)
          const localSort = (
            sort && Array.isArray(sort) ? sort : [sort].filter(Boolean)
          ) as IDataProviderSort[]
          const result: {
            Data: TSpecializationEligibilityCriteriaListItem[]
            Errors: string
          } = {
            Data: [],
            Errors: '',
          }
          try {
            result.Data = await this.fetchList({
              sort: localSort.length ? localSort : undefined,
              kendoConditions: filter,
              page: pagination.page,
              size: pagination.pageSize,
            })
          } catch (e: any) {
            result.Errors = e.message || 'Failed'
          }

          return result
        },
      }),
      keyField: 'EntryId',
      defaultPaging: {
        page: 1,
        pageSize: 10,
        startPage: 1,
      },
    })
  }

  public composeListSpecializationWithoutCriteriaCrud(requestType: RequestTypes) {
    return new Crud<IRequestSpecialization, number>({
      fetcher: new ProcedureInnerStorageFetcher('EntryId', {
        readFromTo: async (filter, sort, paging) => {
          const pagination = convertToServerPaginationIfNot(paging)
          const result: {
            Data: IRequestSpecialization[]
            Errors: string
          } = {
            Data: [],
            Errors: '',
          }
          try {
            result.Data = await this.fetchSpecializationListWithoutCriteria({
              page: pagination.page,
              size: pagination.pageSize,
              kendoConditions: filter.filters.length ? filter : undefined,
              requestType,
            })
          } catch (e: any) {
            result.Errors = e.message || 'Failed'
          }

          return result
        },
      }),
      keyField: 'Name',
      defaultPaging: {
        page: 1,
        pageSize: 1000,
        startPage: 1,
      },
    })
  }

  public async fetchByIdExtended(id: number) {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{
      item: TSpecializationEligibilityCriteriaWithName | null
    }>({
      service: CONTROLLER,
      action: 'fetchByIdExtended',
      payload: { id },
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed')
    }

    return response.data.item
  }

  public async existsActive(specializationCode: string, requestType: RequestTypes) {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{
      isExists: boolean
    }>({
      service: CONTROLLER,
      action: 'existsActive',
      payload: { specializationCode, requestType },
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed')
    }

    return response.data.isExists
  }

  public async fetchActiveByCodeAndTypeExtended(
    specializationCode: string,
    requestType: RequestTypes
  ) {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{
      item: TSpecializationEligibilityCriteriaWithName | null
    }>({
      service: CONTROLLER,
      action: 'fetchActiveByCodeAndTypeExtended',
      payload: { specializationCode, requestType },
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed')
    }

    return response.data.item
  }

  public async fetchRevenueCriteriaThresholds(specializationCode: string, countryISO: string) {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{
      item: TRevenueCriteriaThresholds | null
    }>({
      service: CONTROLLER,
      action: 'fetchRevenueCriteriaThresholds',
      payload: { specializationCode, countryISO },
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed')
    }

    return response.data.item
  }

  public async fetchSpecializationListWithoutCriteria(
    payload: Partial<Pick<TFetchListPayload, 'page' | 'size' | 'kendoConditions'>> & {
      requestType: RequestTypes
    }
  ) {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{ items: IRequestSpecialization[] }>({
      service: CONTROLLER,
      action: 'listSpecializationWithoutCriteria',
      payload,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed')
    }

    return response.data.items
  }

  public async fetchList(payload: TFetchListPayload) {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{ items: TSpecializationEligibilityCriteriaListItem[] }>({
      service: CONTROLLER,
      action: 'listShortened',
      payload,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to load list')
    }

    return response.data.items
  }

  public async create(payload: TCreateCriteriaPayload) {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{ item: TSpecializationEligibilityCriteriaWithName }>({
      service: CONTROLLER,
      action: 'create',
      payload,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to create')
    }

    return response.data.item
  }

  public async update(id: number, payload: TUpdateCriteriaPayload) {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{ item: TSpecializationEligibilityCriteriaWithName }>({
      service: CONTROLLER,
      action: 'update',
      payload: { id, payload },
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to update')
    }

    return response.data.item
  }

  public async delete(id: number) {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call({
      service: CONTROLLER,
      action: 'delete',
      payload: { id },
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to delete')
    }

    return true
  }
}

export type TFetchListPayload = {
  kendoConditions: IDataProviderFilter
  sort?: IDataProviderSort[]
  page: number
  size: number
}

export const specializationEligibilityCriteriaApi = new SpecializationEligibilityCriteriaApi()
